export const ScreenGroupEnum = {
  OnlineShop: '/online-shop',
  Marketing: '/marketing',
  Reservations: '/reservations',
  PhoneAnswering: '/phone-answering',
  Website: '/website',
  OrdersPortal: '/orders-portal',
  ROI: '/roi',
  MobileApp: '/mobile-app',
  Team: '/team',
  Pricing: '/pricing',
  DataDeletionRequests: '/data-deletion-requests',
  // Rail-only group: the Developers screen itself lives under Online Shop, this key
  // exists so the bottom rail entry passes the canAccessScreenGroup gate.
  Developers: '/developers',
  // The partner's own account, branding, billing and tax defaults — its own rail
  // section rather than items inside Online Shop, because none of it is about a
  // restaurant. Reachable only by partner (non-master) Admins; see
  // PartnerAdminScreens.
  Partner: '/partner',
  // Rail-only group for FoodAmigos staff: report a tech issue for the selected
  // restaurant to Maxwell, the triage agent.
  ReportTechIssue: '/tech-issues',
};

export const ScreenEnum = {
  Login: '/login',
  ForgotPassword: '/forgot-password',
  ResetPassword: '/reset-password',
  CreateNewPassword: '/create-new-password',
  Home: `${ScreenGroupEnum.OnlineShop}/`,
  Profile: `${ScreenGroupEnum.OnlineShop}/profile`,
  GetStarted: `${ScreenGroupEnum.OnlineShop}/get-started`,
  Dashboard: `${ScreenGroupEnum.OnlineShop}/dashboard`,
  TopItems: `${ScreenGroupEnum.OnlineShop}/top-items`,
  GiftCards: `${ScreenGroupEnum.OnlineShop}/gift-cards`,
  CustomerBase: `${ScreenGroupEnum.OnlineShop}/customer-base`,
  Companies: `${ScreenGroupEnum.OnlineShop}/companies`,
  NewRestaurant: `${ScreenGroupEnum.OnlineShop}/restaurants/new`,
  RestaurantOnboarding: `${ScreenGroupEnum.OnlineShop}/restaurants/onboarding`,
  AgentOpsQueue: `${ScreenGroupEnum.OnlineShop}/agent-suggestions`,
  FeatureFlags: `${ScreenGroupEnum.OnlineShop}/feature-flags`,
  Partners: `${ScreenGroupEnum.OnlineShop}/partners`,
  TestRestaurants: `${ScreenGroupEnum.OnlineShop}/test-restaurants`,
  PaymentGateways: `${ScreenGroupEnum.OnlineShop}/payment-gateways`,
  ExternalDeliverySupport: `${ScreenGroupEnum.OnlineShop}/external-delivery-support`,
  Franchises: `${ScreenGroupEnum.OnlineShop}/franchises`,
  SetupPackageFulfillments: `${ScreenGroupEnum.OnlineShop}/setup-package-fulfillments`,

  WebsiteStorefront: `${ScreenGroupEnum.Website}/storefront`,
  WebsiteStudio: `${ScreenGroupEnum.Website}/studio`,
  WebsiteStudioV2: `${ScreenGroupEnum.Website}/studio-v2`,

  MarketingOverview: `${ScreenGroupEnum.Marketing}/overview`,
  MarketingFollowers: `${ScreenGroupEnum.Marketing}/followers`,
  MarketingFollowersImport: `${ScreenGroupEnum.Marketing}/followers/import`,
  MarketingEmailMessages: `${ScreenGroupEnum.Marketing}/email-messages`,
  MarketingEmailTemplate: `${ScreenGroupEnum.Marketing}/email-template`,
  MarketingPromoCodes: `${ScreenGroupEnum.Marketing}/promo-codes`,
  MarketingPromoCodeDetails: `${ScreenGroupEnum.Marketing}/promo-codes/:id/details`,
  MarketingGoogleReviewProgram: `${ScreenGroupEnum.Marketing}/google-review-program`,
  MarketingGoogleBusiness: `${ScreenGroupEnum.Marketing}/google-business`,
  MarketingLoyaltyProgram: `${ScreenGroupEnum.Marketing}/loyalty-program`,
  MarketingPolygraphyProducts: `${ScreenGroupEnum.Marketing}/polygraphy/products`,
  MarketingPolygraphyOrders: `${ScreenGroupEnum.Marketing}/polygraphy/orders`,
  MarketingTrackingLinks: `${ScreenGroupEnum.Marketing}/tracking-links`,
  MarketingGoogleAds: `${ScreenGroupEnum.Marketing}/google-ads`,
  MarketingWebsiteAnalytics: `${ScreenGroupEnum.Marketing}/website-analytics`,
  MarketingFeaturedPosts: `${ScreenGroupEnum.Marketing}/featured-posts`,
  MarketingPushNotifications: `${ScreenGroupEnum.Marketing}/push-notifications`,
  MarketingPushNotificationDetails: `${ScreenGroupEnum.Marketing}/push-notifications/:id/details`,

  ReservationsOverview: `${ScreenGroupEnum.Reservations}/overview`,
  ReservationsCreate: `${ScreenGroupEnum.Reservations}/create`,
  ReservationsSettings: `${ScreenGroupEnum.Reservations}/settings`,
  ReservationsBookingAvailability: `${ScreenGroupEnum.Reservations}/booking-availability`,

  PhoneAnswering: ScreenGroupEnum.PhoneAnswering,

  Developers: `${ScreenGroupEnum.OnlineShop}/developers`,

  // Partner section. Account and Branding were one "Partner settings" page until
  // the nav rework split them into siblings; the two cards were already
  // independent (separate load + save calls), so the split is a routing change.
  PartnerAccount: `${ScreenGroupEnum.Partner}/account`,
  PartnerBranding: `${ScreenGroupEnum.Partner}/branding`,
  PartnerPaymentGateway: `${ScreenGroupEnum.Partner}/payment-gateway`,
  PartnerBilling: `${ScreenGroupEnum.Partner}/billing`,
  PartnerTaxCurrency: `${ScreenGroupEnum.Partner}/tax-currency`,
  PartnerServiceFeeCalculator: `${ScreenGroupEnum.Partner}/service-fee-calculator`,
  PartnerProcessingFees: `${ScreenGroupEnum.Partner}/processing-fees`,

  SalesOrders: `${ScreenGroupEnum.OnlineShop}/orders`,
  SalesReceipts: `${ScreenGroupEnum.OnlineShop}/receipts`,
  SalesExternalDeliveries: `${ScreenGroupEnum.OnlineShop}/external-deliveries`,

  BillingPaymentInfo: `${ScreenGroupEnum.OnlineShop}/payment-info`,
  BillingMonthlyStatements: `${ScreenGroupEnum.OnlineShop}/monthly-statements`,
  BillingPayouts: `${ScreenGroupEnum.OnlineShop}/payouts`,
  BillingInvoices: `${ScreenGroupEnum.OnlineShop}/invoices`,
  BillingSubscription: `${ScreenGroupEnum.OnlineShop}/subscription`,
  BillingIncomeReports: `${ScreenGroupEnum.OnlineShop}/income-reports`,
  BillingTaxReports: `${ScreenGroupEnum.OnlineShop}/tax-reports`,
  BillingSalesSummary: `${ScreenGroupEnum.OnlineShop}/sales-summary`,

  SubscriptionProducts: `${ScreenGroupEnum.OnlineShop}/subscription-products`,

  MasterOverview: `${ScreenGroupEnum.OnlineShop}/master-overview`,
  MasterMenu: `${ScreenGroupEnum.OnlineShop}/master-menus`,
  MasterModifiers: `${ScreenGroupEnum.OnlineShop}/master-modifiers`,
  MasterSmartPricing: `${ScreenGroupEnum.OnlineShop}/master-smart-pricing`,
  MasterMenuImport: `${ScreenGroupEnum.OnlineShop}/master-menu-import`,
  MasterOffers: `${ScreenGroupEnum.OnlineShop}/master-offers`,
  MasterCreateOffers: `${ScreenGroupEnum.OnlineShop}/master-offers/create`,
  MasterUpdateOffers: `${ScreenGroupEnum.OnlineShop}/master-offers/update`,

  Menu: `${ScreenGroupEnum.OnlineShop}/menu`,
  MenuEditHistory: `${ScreenGroupEnum.OnlineShop}/menu-edit-history`,
  Modifiers: `${ScreenGroupEnum.OnlineShop}/modifiers`,
  SmartPricing: `${ScreenGroupEnum.OnlineShop}/smart-pricing`,
  SoldOutItems: `${ScreenGroupEnum.OnlineShop}/sold-out-items`,
  MenuImport: `${ScreenGroupEnum.OnlineShop}/menu-import`,
  Offers: `${ScreenGroupEnum.OnlineShop}/offers`,
  CreateOffers: `${ScreenGroupEnum.OnlineShop}/offers/create`,
  UpdateOffers: `${ScreenGroupEnum.OnlineShop}/offers/update`,

  MyStoreSettings: `${ScreenGroupEnum.OnlineShop}/settings`,
  MyStoreGallery: `${ScreenGroupEnum.OnlineShop}/gallery`,
  MyStoreBusinessInfo: `${ScreenGroupEnum.OnlineShop}/business-info`,
  MyStorePersonalInfo: `${ScreenGroupEnum.OnlineShop}/personal-info`,
  MyStoreBankInfo: `${ScreenGroupEnum.OnlineShop}/bank-info`,
  MyStoreTaxInfo: `${ScreenGroupEnum.OnlineShop}/tax-info`,
  MyStoreShippingInfo: `${ScreenGroupEnum.OnlineShop}/shipping-info`,
  MyStoreGoogleProfile: `${ScreenGroupEnum.OnlineShop}/google-profile`,
  MyStorePostLaunch: `${ScreenGroupEnum.OnlineShop}/post-launch`,
  MyStoreSeo: `${ScreenGroupEnum.OnlineShop}/seo`,
  MyStoreImpressum: `${ScreenGroupEnum.OnlineShop}/impressum`,
  MyStoreImprint: `${ScreenGroupEnum.OnlineShop}/imprint`,
  MyStoreDocuments: `${ScreenGroupEnum.OnlineShop}/documents`,
  MyStoreTaxCurrency: `${ScreenGroupEnum.OnlineShop}/tax-currency`,

  OpeningHours: `${ScreenGroupEnum.OnlineShop}/opening-hours`,
  Locations: `${ScreenGroupEnum.OnlineShop}/locations`,

  OrderSettingsTakeout: `${ScreenGroupEnum.OnlineShop}/takeout`,
  OrderSettingsMissedOrders: `${ScreenGroupEnum.OnlineShop}/missed-orders`,
  OrderSettingsOrderLayout: `${ScreenGroupEnum.OnlineShop}/order-layout`,
  OrderSettingsPaymentMethods: `${ScreenGroupEnum.OnlineShop}/payment-methods`,
  OrderSettingsConnectPaypal: `${ScreenGroupEnum.OnlineShop}/connect-paypal`,
  OrderSettingsTipSettings: `${ScreenGroupEnum.OnlineShop}/tip-settings`,
  OrderSettingsServiceFee: `${ScreenGroupEnum.OnlineShop}/service-fee`,
  OrderSettingsProcessingFees: `${ScreenGroupEnum.OnlineShop}/processing-fees`,
  OrderSettingsTerminal: `${ScreenGroupEnum.OnlineShop}/terminal`,
  OrderSettingsReceiptSettings: `${ScreenGroupEnum.OnlineShop}/receipt-settings`,
  OrderSettingsInvoicesSettings: `${ScreenGroupEnum.OnlineShop}/invoices-settings`,
  OrderSettingsStaffCredentials: `${ScreenGroupEnum.OnlineShop}/staff-credentials`,
  OrderSettingsIntegrations: `${ScreenGroupEnum.OnlineShop}/integrations`,

  LoyaltyDashboard: `${ScreenGroupEnum.OnlineShop}/loyalty-dashboard`,

  ContractAgreement: `${ScreenGroupEnum.OnlineShop}/contract-agreement`,
  ContractTemplate: `${ScreenGroupEnum.OnlineShop}/contract-template`,

  TeamManagers: `${ScreenGroupEnum.Team}/managers`,
  TeamReports: `${ScreenGroupEnum.Team}/reports`,

  MobileAppOverview: `${ScreenGroupEnum.MobileApp}/overview`,
  MobileAppUpdates: `${ScreenGroupEnum.MobileApp}/updates`,
  MobileAppIntroScreen: `${ScreenGroupEnum.MobileApp}/intro-screen`,

  DataDeletionRequests: ScreenGroupEnum.DataDeletionRequests,

  ReportTechIssue: `${ScreenGroupEnum.ReportTechIssue}/report`,
  ReportTechIssueOverview: `${ScreenGroupEnum.ReportTechIssue}/overview`,
};

/**
 * Paths that used to serve a screen and now only redirect to its new home.
 * Partners were sent these URLs directly (Stripe return URLs, support links,
 * bookmarks), so they keep working rather than dropping into the 404 route.
 *
 * Not part of ScreenEnum: nothing may link *to* these, and they must never
 * appear in a permission list or the sidebar.
 */
export const LegacyScreenEnum = {
  PartnerSettings: `${ScreenGroupEnum.OnlineShop}/partner-settings`,
  PartnerBilling: `${ScreenGroupEnum.OnlineShop}/partner-billing`,
  PartnerTaxCurrency: `${ScreenGroupEnum.OnlineShop}/partner-tax-currency`,
};
